// src/components/MyPage/index.jsx
import { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, CardActionArea, Container, CircularProgress,
  Dialog, DialogTitle, DialogContent, IconButton, Typography, DialogActions, Button, Stack,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import ArticleIcon from '@mui/icons-material/Article';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import NotificationsIcon from '@mui/icons-material/Notifications';
import InventoryIcon from '@mui/icons-material/Inventory2';
import HistoryIcon from '@mui/icons-material/History';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SettingsIcon from '@mui/icons-material/Settings';

import UserSummary from './UserSummary';
import MyBookmarks from './MyBookmarks';
import MyItems from './MyItems';
import ProfileCustomizer from './ProfileCustomizer';
import CombinedHistory from './CombinedHistory';
import UserSettings from './UserSettings';
import PostsComments from './PostsComments';
import NotificationsPanel from './NotificationsPanel';
//import HardReloadButton from '../Common/HardReloadButton';   // ⬅️ 추가
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import MiniroomView from '../Miniroom/MiniroomView'; // ← 앞서 준 읽기 전용 뷰


import { useUser } from '../../contexts/UserContext';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import PaletteIcon from '@mui/icons-material/Palette';
import api from '../../api/axios';

const CARD_W = 120;
const CARD_H = 160;
const ICON_SIZE = 36;
const SHOW_MINIROOM = false;   // 개발 중이라면 false

const buildMetrics = (counts, refreshItems, username, navigate, onMinihomeClick) => ({
  activity: {
    icon: <ArticleIcon />,
    label: '글·댓글',
    count: (counts.posts ?? 0) + (counts.comments ?? 0),
    component: <PostsComments />,
  },
  bookmarks: {
    icon: <BookmarkIcon />,
    label: '북마크',
    count: counts.bookmarks ?? 0,
    component: <MyBookmarks />,
  },
  minihome: {
    icon: <HomeIcon />,
    label: '미니홈',
    count: null,
    // ⬇️ 클릭 시 바로 이동하지 않고 메뉴 오픈
    onCardClick: () => onMinihomeClick(),
    // ⬇️ 별도의 보조액션(연필/팔레트 버튼) 제거 — 메뉴로 통일
    // extraAction: undefined
  },
  miniroom: {  // ← 동위 키로 분리 
    icon: <MeetingRoomIcon />,
    label: '미니룸',
    count: null,
    component: <MiniroomView />,
    extraAction: {
      icon: <PaletteIcon fontSize="small" />,
      to: '/me/room/edit',                 // ← 미니룸은 Link로 이동
      aria: '미니룸 꾸미기',
    },
  },


  items: {
    icon: <InventoryIcon />,
    label: '보유 아이템',
    count: counts.items ?? 0,
    component: <MyItems onChanged={refreshItems} />,
  },
  appearance: {
    icon: <EmojiEventsIcon />,
    label: '프로필 꾸미기',
    count: null,
    component: <ProfileCustomizer />,
  },
  notifications: {
    icon: <NotificationsIcon />,
    label: '소식함',
    count: counts.notifications ?? 0,
    component: <NotificationsPanel />,
  },
  history: {
    icon: <HistoryIcon />,
    label: '내역',
    count: null,
    component: <CombinedHistory />,
  },
  user_settings: {
    icon: <SettingsIcon />,
    label: '회원정보 수정',
    count: null,
    component: <UserSettings />,
  },
});

const GROUP_ORDER = [
  ['activity', 'bookmarks', 'minihome', ...(SHOW_MINIROOM ? ['miniroom'] : [])], //['activity', 'bookmarks', 'minihome', 'miniroom'],
  ['items', 'notifications'],
  ['history', 'appearance', 'user_settings']
];

const MyPage = () => {
  const { userInfo, refreshUserInfo } = useUser();
  const location = useLocation();
  const navigate = useNavigate();

  const [counts, setCounts] = useState({});
  const [open, setOpen] = useState(false);
  const [activeKey, setActive] = useState('activity');

  const [miniHomeMenuOpen, setMiniHomeMenuOpen] = useState(false);

  // ✅ 여기에 붙여넣기
  const openMinihomeMenu = () => setMiniHomeMenuOpen(true);
  const closeMinihomeMenu = () => setMiniHomeMenuOpen(false);

  const goMyMinihome = () => {
    closeMinihomeMenu();
    navigate(`/u/${encodeURIComponent(userInfo.username)}/home`);
  };

  const goEditMinihome = () => {
    closeMinihomeMenu();
    navigate('/me/home/edit');
  };

  const fetchMetrics = useCallback(() => {
    api.get('/api/metrics/')
      .then(r => setCounts(r.data))
      .catch(() => setCounts({}));
  }, []);

  useEffect(() => {
    refreshUserInfo();
    fetchMetrics();
  }, [refreshUserInfo, fetchMetrics]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');

    if (tabParam === 'timeline' || tabParam === 'notifications') {
      setActive('notifications');
      setOpen(true);
    }

    if (tabParam === 'settings') {
      setActive('notifications');
      setOpen(true);
      setTimeout(() => {
        const evt = new Event('open-settings-tab');
        window.dispatchEvent(evt);
      }, 100);
    }
  }, [location.search]);

  if (!userInfo) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const metrics = buildMetrics(counts, fetchMetrics, userInfo?.username, navigate, openMinihomeMenu);
  const activeMetric = metrics[activeKey];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={4}>
        <UserSummary />
      </Box>

      <Box sx={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
        {GROUP_ORDER.map(group => (
          <Box
            key={group.join('-')}
            sx={{
              display: 'flex',
              gap: { xs: 1.5, sm: 2, md: 2 },
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            {group.map(k => {
              const m = metrics[k];
              return (
                <Card
                  key={k}
                  sx={{
                    borderRadius: 3,
                    width: { xs: 100, sm: 110, md: CARD_W },
                    height: { xs: 130, sm: 150, md: CARD_H },
                    display: 'flex',
                    position: 'relative', // 보조 버튼 배치용
                  }}
                >
                  <CardActionArea
                    onClick={() => {
                      // ⬇️ minihome 카드만 메뉴 오픈
                      if (m.onCardClick) return m.onCardClick();
                      if (m.onClick) return m.onClick();
                      setActive(k); setOpen(true);
                    }}
                    sx={{
                      p: { xs: 1.5, md: 2 },
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Box sx={{ fontSize: { xs: 24, sm: 30, md: ICON_SIZE }, lineHeight: 1 }}>
                      {m.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}
                    >
                      {m.count ?? ''}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {m.label}
                    </Typography>
                  </CardActionArea>
                  {/* 보조 액션(편집): 카드 클릭과 구분되도록 stopPropagation */}
                  {m.extraAction && (
                    <IconButton
                      size="small"
                      aria-label={m.extraAction.aria || 'extra-action'}
                      component={m.extraAction.to ? Link : 'button'}
                      to={m.extraAction.to || undefined}
                      onClick={(e) => { e.stopPropagation(); if (m.extraAction.onClick) m.extraAction.onClick(); }}
                      sx={{
                        position: 'absolute',
                        bottom: 6,
                        left: '50%', // 왼쪽에서 50% 위치로 이동
                        transform: 'translateX(-50%)' // 자신의 너비의 50%만큼 왼쪽으로 이동하여 중앙 정렬
                      }}
                    >
                      {m.extraAction.icon}
                    </IconButton>
                  )}
                </Card>
              );
            })}
          </Box>
        ))}
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ pl: 3, pr: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {activeMetric.icon}
            <Typography variant="subtitle1" fontWeight={600} whiteSpace="nowrap">
              {activeMetric.label}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            size="small"
            onClick={() => setOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, width: 32, height: 32 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {activeMetric.component}
        </DialogContent>
      </Dialog>
      {/* ⬇️ 미니홈 이동/편집 선택 다이얼로그 */}
      <Dialog open={miniHomeMenuOpen} onClose={closeMinihomeMenu} maxWidth="xs" fullWidth>
        <DialogTitle>
          미니홈
          <IconButton
            aria-label="close"
            size="small"
            onClick={closeMinihomeMenu}
            sx={{ position: 'absolute', right: 8, top: 8, width: 32, height: 32 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              원하는 작업을 선택하세요.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={goMyMinihome}>
            내 미니홈
          </Button>
          <Button variant="contained" onClick={goEditMinihome}>
            미니홈 편집
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default MyPage;
